import { FunctionComponent, memo, useContext, useMemo, useState } from "react";
import { DetailsSection, DetailsSectionTitle } from "@edgetier/client-components";
import TagGroup from "./tag-group";
import { groupTags } from "utilities/group-tags";
import TagsActionsMenu from "./tags-actions-menu";
import { InteractionContext } from "components-for/interactions/interaction-context/interaction-context";
import SelectTagGroupsForm from "./select-tag-groups-form";
import VerifyPermission from "components-for/pages/verify-permission";
import useTags from "hooks-for/tags/use-tags";
import {
    IInteractionDetailsResponse,
    IInteractionTagsRequest,
    ITag,
    Permission,
    Route,
} from "@edgetier/watchtower-types";
import { useQueryClient } from "react-query";
import toast from "react-hot-toast";
import Axios from "axios";
import { getTimeToRead } from "@edgetier/utilities";
import { axios } from "utilities/axios";
import { showServerError } from "utilities/show-server-error";
import { IProps, IInteractionTagGroup } from "./interaction-tags.types";
import {
    formatTagIdsForRemoveTagGroup,
    formatTagIdsForUpdateTagGroup,
    reorderInteractionTagGroups,
} from "./interaction-tags.utilities";
import { EDIT_INTERACTION_TAGS_ENABLED } from "constants/feature-flags";
import "./interaction-tags.scss";

/**
 * Display the tags relating to an interaction.
 * @param props.interactionId
 * @param props.tags The tags to display.
 */
const InteractionTags: FunctionComponent<IProps> = ({ interactionId, tags }) => {
    const { data: allTags } = useTags();
    const { isAddTagGroupSelectVisible } = useContext(InteractionContext);
    const queryClient = useQueryClient();

    const [temporaryTagGroups, setTemporaryTagGroups] = useState<IInteractionTagGroup>({});
    const [temporaryTagGroupNames, setTemporaryTagGroupNames] = useState<string[]>([]);

    // Group all tags
    const groupedAllTags: IInteractionTagGroup = useMemo(() => Object.fromEntries(groupTags(allTags ?? [])), [allTags]);
    // Group interaction tags
    const groupedInteractionTags: IInteractionTagGroup = useMemo(() => Object.fromEntries(groupTags(tags)), [tags]);

    /**
     * Remove temporary tag group from state
     * @param tagGroup  Tag group name
     */
    const removeTemporaryTagGroup = (tagGroup: string) => {
        // Remove any temporary tags after tags were added to it
        const { [tagGroup]: removedTemporaryTag, ...restTagGroups } = temporaryTagGroups;
        setTemporaryTagGroups(restTagGroups);
    };

    /**
     * Add or remove tags for a tag group
     * @param tagGroup  Tag group name for selected tags
     * @param tagIds    Selected tag ids
     */
    const updateInteractionTags = async (tagGroup: string, tagIds: string[]) => {
        try {
            const queryUrl = Route.InteractionTags.replace(":interactionId", interactionId);

            // Tag ids intended for updating the interaction tags
            const { postTagIds, deleteTagIds, differentTagGroupTags } = formatTagIdsForUpdateTagGroup(
                tagGroup,
                tagIds,
                tags
            );

            // DELETE and POST requests. Make the call when there are any tag ids to be passed
            await Promise.all([
                ...(deleteTagIds.length > 0
                    ? [axios.delete<IInteractionTagsRequest>(queryUrl, { data: { items: deleteTagIds } })]
                    : []),
                ...(postTagIds.length > 0
                    ? [axios.post<IInteractionTagsRequest>(queryUrl, { items: postTagIds })]
                    : []),
            ]);

            // Recreate interaction tags
            const updatedInteractionTags = [
                ...differentTagGroupTags,
                ...groupedAllTags[tagGroup].filter((tag) => tagIds.includes(tag.tagId)),
            ];

            // Update interaction details with the new changes
            queryClient.setQueryData<IInteractionDetailsResponse | undefined>(
                [Route.InteractionDetails, interactionId],
                (activeInteraction) => {
                    if (typeof activeInteraction !== "undefined") {
                        return { ...activeInteraction, tags: updatedInteractionTags };
                    }

                    return activeInteraction;
                }
            );

            // Remove any temporary tags after tags were added to it
            removeTemporaryTagGroup(tagGroup);

            const successMessage = `Tags successfully updated`;
            toast.success(successMessage, { duration: getTimeToRead(successMessage, 1000) });
        } catch (serverError) {
            if (Axios.isAxiosError(serverError)) {
                showServerError(serverError);
            } else {
                toast.error("Failed to update tags");
            }
        }
    };

    /**
     * Remove all tags from a tag group
     * @param tagGroup
     */
    const removeTagGroup = async (tagGroup: string) => {
        try {
            const queryUrl = Route.InteractionTags.replace(":interactionId", interactionId);

            const { deleteTagIds, differentTagGroupTags } = formatTagIdsForRemoveTagGroup(tagGroup, tags);

            // Find the tag group in the temporary tags
            const { [tagGroup]: removedTemporaryTag } = temporaryTagGroups;
            const isTemporaryTagGroup = typeof removedTemporaryTag !== "undefined";

            // Make the DELETE request only if the tag group is not temporary, i.e. doesn't have any tags added yet
            // When a tag is added to a temporary tag group, the POST is made to API and that tag is permanent
            if (!isTemporaryTagGroup) {
                // DELETE request
                if (deleteTagIds.length > 0) {
                    await axios.delete<IInteractionTagsRequest>(queryUrl, { data: { items: deleteTagIds } });
                }
            } else {
                removeTemporaryTagGroup(tagGroup);
            }
            if (temporaryTagGroupNames.includes(tagGroup)) {
                setTemporaryTagGroupNames((newTags) => newTags.filter((tagGroupName) => tagGroupName !== tagGroup));
            }

            // Update interaction details with the new changes
            queryClient.setQueryData<IInteractionDetailsResponse | undefined>(
                [Route.InteractionDetails, interactionId],
                (activeInteraction) => {
                    if (typeof activeInteraction !== "undefined") {
                        return { ...activeInteraction, tags: differentTagGroupTags };
                    }

                    return activeInteraction;
                }
            );
            if (!isTemporaryTagGroup) {
                const successMessage = `Tags from "${tagGroup}" were successfully removed`;
                toast.success(successMessage, { duration: getTimeToRead(successMessage, 1000) });
            }
        } catch (serverError) {
            if (Axios.isAxiosError(serverError)) {
                showServerError(serverError);
            } else {
                toast.error("Failed to remove tags");
            }
        }
    };

    /**
     * Add a tag group as temporary select
     * @param tagGroup
     */
    const addNewTagGroup = (tagGroup: string) => {
        setTemporaryTagGroups((tags) => ({
            ...tags,
            [tagGroup]: [] as ITag[],
        }));

        setTemporaryTagGroupNames(Array.from(new Set([...temporaryTagGroupNames, tagGroup])));
    };

    const allInteractionTagGroups: IInteractionTagGroup = useMemo(
        () => reorderInteractionTagGroups({ ...temporaryTagGroups, ...groupedInteractionTags }, temporaryTagGroupNames),
        [groupedInteractionTags, temporaryTagGroupNames, temporaryTagGroups]
    );

    return (
        <div className="interaction-tags">
            <DetailsSection>
                <DetailsSectionTitle title="Tags">
                    {EDIT_INTERACTION_TAGS_ENABLED && (
                        <VerifyPermission permission={Permission.ManageInteractionTags}>
                            <TagsActionsMenu />
                        </VerifyPermission>
                    )}
                </DetailsSectionTitle>

                <div className="interaction-tags__container">
                    {isAddTagGroupSelectVisible && (
                        <SelectTagGroupsForm
                            selectedTagGroups={Object.keys(groupedInteractionTags).map((tagGroup) => tagGroup)}
                            onSelectTagGroup={addNewTagGroup}
                        />
                    )}

                    {Object.entries(allInteractionTagGroups).map(([tagGroup, listTags], index) => (
                        <TagGroup
                            key={`${tagGroup}-${index}`}
                            groupTags={groupedAllTags[tagGroup]}
                            updateInteractionTags={updateInteractionTags}
                            removeTagGroup={removeTagGroup}
                            interactionTags={listTags}
                            tagGroup={tagGroup}
                        />
                    ))}
                </div>
            </DetailsSection>
        </div>
    );
};

export default memo(InteractionTags);
